<template>
  <div class="nlp">
    <popup :title="$t('view.settings.popup.unsaved.title')" ref="popupEditGuard">
      <div class="popup-subtext">{{ $t('view.settings.popup.unsaved.subtext') }}</div>
      <template v-slot:buttons>
        <btn icon="trash" color="red" @click="performViewAction()">{{ $t('btn.exit') }}</btn>
        <btn icon="undo" @click="cancelViewAction()">{{ $t('btn.cancel') }}</btn>
      </template>
    </popup>
    <div class="nlp__sidebar">
      <div class="nlp__sidebar-intents">
        <div class="nlp__sidebar-intents-title">{{ $t('plugins.nlp.intent.title') }}</div>
        <div class="nlp__sidebar-intents-cont">
          <div class="nlp__sidebar-intents-item" v-for="(item, index) in intents" :key="index">
            <short-card
              class="nlp__sidebar-intents-card"
              v-model="intents[index].intents"
              @selected="guardEdited(getSelected.bind(this, item))"
              :isSelected="isSelected(item)"
            >
              <div>{{ item.name }}</div>
            </short-card>
          </div>
          <span
            class="nlp__sidebar-create"
            @click="guardEdited(showCreate)"
            >{{ $t('plugins.nlp.intent.newTitle') }}
          </span>
        </div>
      </div>
      <div class="nlp__sidebar-menu">
        <div class="nlp__sidebar-menu-divider"></div>
        <div class="nlp__sidebar-menu-item"
          :class="whichView === 1 ? 'nlp__sidebar-menu-item--active' : ''"
          @click="guardEdited(showEntities)">
          <div class="nlp__sidebar-menu-color"></div>
          <div class="nlp__sidebar-menu-name">
            {{ $t('plugins.nlp.entities.edit') }}
          </div>
        </div>
        <div class="nlp__sidebar-menu-divider"></div>
        <div class="nlp__sidebar-menu-item"
          :class="whichView === 2 ? 'nlp__sidebar-menu-item--active' : ''"
          @click="guardEdited(showSavedPhrases)">
          <div class="nlp__sidebar-menu-color"></div>
          <div class="nlp__sidebar-menu-name">
            {{ $t('plugins.nlp.savedPhrases.viewButton') }}
          </div>
        </div>
        <div class="nlp__sidebar-menu-divider"></div>
        <div class="nlp__sidebar-menu-item"
          :class="whichView === 3 ? 'nlp__sidebar-menu-item--active' : ''"
          @click="guardEdited(showSettings)">
          <div class="nlp__sidebar-menu-color"></div>
          <div class="nlp__sidebar-menu-name">
            {{ $t('plugins.nlp.settings.viewButton') }}
          </div>
        </div>
        <div class="nlp__sidebar-menu-divider"></div>
      </div>
      <div class="nlp__test">
        <div ref="refTestPopup"
          class="nlp__test-popup"
          v-show="showTestResult">
          <div class="nlp__test-color">
          </div>
          <div class="nlp__test-header">
            <fai icon="times"
              class="nlp__test-header-icon"
              @click="showTestResult = false"/>
          </div>
          <div class="nlp__test-content">
            <div class="nlp__test-label">
              Fuzzied:
            </div>
            <div class="nlp__test-text">
              {{ testResult.fuzzied }}
            </div>
            <div class="nlp__test-label">
              Phrase:
            </div>
            <div class="nlp__test-text">
              {{ testResult.phrase }}
            </div>
            <div class="nlp__test-conf-header">
              Confidences:
            </div>
            <div class="nlp__test-conf-content">
              <div class="nlp__test-conf-empty" v-if="testResult.confidences.length === 0">
                no confidences
              </div>
              <div class="nlp__test-conf-name"
                v-for="(item, index) in testResult.confidences" :key="index+'name'">
                {{ item.name + ':' }}
              </div>
              <div class="nlp__test-conf-value"
                v-for="(item, index) in testResult.confidences" :key="index+'val'">
                {{ item.confidence }}
              </div>
            </div>
          </div>
        </div>
        <in
          class="nlp__test-input"
          :placeholder="$t('plugins.nlp.entities.test')"
          v-model="testStr"
          v-on:keyup.enter="processTestPhrase"
        />
      </div>
    </div>
    <intent-editor
      class="nlp__editor"
      v-model="selectedIntent"
      :entities="entities"
      :states="states"
      :create="intentCreate"
      @deleteIntent="deleteIntent"
      @input="onIntentInput"
      v-if="whichView === 0"
    ></intent-editor>
    <entities-editor class="nlp__editor" v-model="entities" v-if="whichView === 1"></entities-editor>
    <saved-phrases class="nlp__editor" :intents="intents" :entities="entities" v-if="whichView === 2"></saved-phrases>
    <nlp-settings 
      class="nlp__editor" 
      v-if="whichView === 3"
      :states="states"
      @statesUpdate="states = $event"
      >
    </nlp-settings>
  </div>
</template>

<script>
import axios from '@/axios'
import store from '@/store'
import intent from '../components/Intent.vue'
import entities from '../components/Entities.vue'
import savedPhrases from '../components/SavedPhrases.vue'
import settings from '../components/Settings.vue'
import s_popup from '../components/SelectPopup.vue'
export default {
  data () {
    return {
      selectedIntent: null,
      intents: [],
      entities: {},
      states: [],
      intentCreate: true,
      whichView: 0,
      viewAction: null,
      testStr: '',
      showTestResult: false,
      testResult: {
        confidences: [],
        phrase: '',
        fuzzied: ''
      }
    }
  },

  beforeMount () {
    window.addEventListener('beforeunload', this.preventNavi)
  },

  beforeDestroy () {
    window.removeEventListener('beforeunload', this.preventNavi)
  },

  beforeRouteLeave (to, from, next) {
    if(this.$store.state.isEdited) {
      this.viewAction = next
      this.$refs.popupEditGuard.open()
      next(false)
    } else {
      next()
    }
  },

  mounted: async function () {
    this.$store.dispatch('loadFlowData', this)
    this.fetchNlpData()
  },

  methods: {
    preventNavi (event) {
      if(!this.$store.state.isEdited) return
      event.preventDefault()
      event.returnValue = ""
    },

    fetchNlpData: async function () {
      try {
        const intentsData = await axios.get('/api/plugin/nlp/intents/list')
        const entitiesData = await axios.get('/api/plugin/nlp/entities/list')
        await this.fetchStates()
        this.formatNlpData(intentsData.data, entitiesData.data)
        const formattedStatesTransitions = this.formatStatesTransitions(intentsData.data.statesTransitions, intentsData.data.statesTransitionsStates)
        this.attachStatesTransitionsToIntents(this.intents, formattedStatesTransitions)

      } catch (e) {
        this.notify('error', 'plugins.nlp.error.main.load')
        console.error(e)
      }
    },

    fetchStates: async function () {
      try {
        const res = await axios.get('/api/plugin/nlp/settings/states')

        this.states = res.data.states
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.main.load')
        console.error(e)
      }
    },

    formatNlpData: async function (intentsData, entitiesData) {
      //// =================== 
      //// entities formatting
      // text
      let i = 0
      let len = entitiesData.textEntities.length
      let formattedTextEntities = []
      while (i < len) {
        let entity = {
          id: entitiesData.textEntities[i].entities_id,
          name: entitiesData.textEntities[i].name,
          is_fuzzy: entitiesData.textEntities[i].is_fuzzy,
          priority: entitiesData.textEntities[i].priority,
          entities_entries: []
        }
        while (i < len && entitiesData.textEntities[i].entities_id === entity.id) {
          let entities_entry = {
            id: entitiesData.textEntities[i].entities_entries_id,
            entries_values: []
          }
          while (i < len && entitiesData.textEntities[i].entities_entries_id === entities_entry.id) {
            let entries_value = {
              id: entitiesData.textEntities[i].entries_values_id,
              values_segments: []
            }
            if (entitiesData.textEntities[i].isRefValue) entries_value.isRefValue = true
            while (i < len && entitiesData.textEntities[i].entries_values_id === entries_value.id) {
              let values_segment = {
                ortho: entitiesData.textEntities[i].ortho,
                lemma: entitiesData.textEntities[i].lemma
              }
              entries_value.values_segments.push(values_segment)
              i++
            }
            entities_entry.entries_values.push(entries_value)
          }
          entity.entities_entries.push(entities_entry)
        }
        formattedTextEntities.push(entity)
      }
      delete entitiesData.textEntities
      // regex
      i = 0
      len = entitiesData.regexEntities.length
      let formattedRegexEntities = []
      while (i < len) {
        let entity = {
          id: entitiesData.regexEntities[i].entities_id,
          name: entitiesData.regexEntities[i].name,
          priority: entitiesData.regexEntities[i].priority,
          entities_regexes: []
        }
        while (i < len && entitiesData.regexEntities[i].entities_id === entity.id) {
          let entities_regex = {
            id: entitiesData.regexEntities[i].id,
            exp: entitiesData.regexEntities[i].exp
          }
          entity.entities_regexes.push(entities_regex)
          i++
        }
        formattedRegexEntities.push(entity)
      }
      this.entities = { textEntities: formattedTextEntities, regexEntities: formattedRegexEntities }

      //// ================== 
      //// intents formatting
      let intents = intentsData.intents
      i = 0
      let formattedIntents = []
      len = intents.length
      while (i < len) {
        let intent = {
          id: intents[i].intents_id,
          block_uuid: intents[i].block_uuid,
          name: intents[i].name,
          confidence_save: intents[i].confidence_save,
          confidence_detect: intents[i].confidence_detect,
          states_limit_on: intents[i].states_limit_on,
          training_phrases: [],
          parameters: [],
          states: [],
          statesTransitions: []
        }
        for (let j = 0; j < intentsData.parameters.length; j++) {
          if (intentsData.parameters[j].intents_id === intent.id) {
            intent.parameters.push({
              id: intentsData.parameters[j].id,
              name: intentsData.parameters[j].name,
              active: intentsData.parameters[j].active,
              entities_id: intentsData.parameters[j].entities_id,
              intents_id: intentsData.parameters[j].intents_id
            })
          }
        }
        for (let j = 0; j < intentsData.intentsStates.length; j++) {
          if (intentsData.intentsStates[j].intents_id === intent.id) {
            intent.states.push({
              id: intentsData.intentsStates[j].id,
              states_id: intentsData.intentsStates[j].states_id,
              intents_id: intentsData.intentsStates[j].intents_id,
              negate: intentsData.intentsStates[j].negate
            })
          }
        }
        while (i < len && intents[i].intents_id === intent.id) {
          let phrase = {
            id: intents[i].training_phrases_id,
            intents_id: intent.id,
            segments: []
          }
          while (i < len && intents[i].training_phrases_id === phrase.id) {
            let segment = {
              id: intents[i].id,
              ortho: intents[i].ortho,
              lemma: intents[i].lemma
            }
            if (intents[i].parameters_id) segment.parameters_id = intents[i].parameters_id
            phrase.segments.push(segment)
            i++
          }
          intent.training_phrases.push(phrase)
        }
        formattedIntents.push(intent)
      }
      this.intents = formattedIntents
    },

    formatStatesTransitions (statesTransitionsData, statesTransitionsStatesData) {
      const formattedStatesTransitions = []
      const len = statesTransitionsData.length
      let i = 0
      while (i < len) {
        const stateTransition = {
          id: statesTransitionsData[i].id,
          intents_id: statesTransitionsData[i].intents_id,
          block_uuid: statesTransitionsData[i].block_uuid,
          parameters: [],
          states: []
        }
        while(i < len && stateTransition.id === statesTransitionsData[i].id) {
          if(statesTransitionsData[i].states_transitions_parameters_id) {
            const parameter = {
              id: statesTransitionsData[i].states_transitions_parameters_id,
              parameters_id: statesTransitionsData[i].parameters_id,
              states_transitions_id: statesTransitionsData[i].states_transitions_id
            }
            stateTransition.parameters.push(parameter)
          }
          i++
        }
        formattedStatesTransitions.push(stateTransition)
      }

      for(let i = 0; i<statesTransitionsStatesData.length; i++) {
        for(let j = 0; j<formattedStatesTransitions.length; j++) {
          if(formattedStatesTransitions[j].id === statesTransitionsStatesData[i].states_transitions_id) {
            formattedStatesTransitions[j].states.push(statesTransitionsStatesData[i])
          }
        }
      }

      return formattedStatesTransitions
    },

    attachStatesTransitionsToIntents (formattedIntents, formattedStatesTransitions) {
      for(let i = 0; i<formattedIntents.length; i++) {
        const actIntent = formattedIntents[i]
        for(let j = 0; j<formattedStatesTransitions.length; j++) {
          const actStateTransition = formattedStatesTransitions[j]
          if(actIntent.id === actStateTransition.intents_id) {
            actIntent.statesTransitions.push(actStateTransition)
          }
        }
      }
    },

    deleteIntent: async function () {
      try {
        await axios.delete('/api/plugin/nlp/intents/delete', {
          data: {
            intentsIds: [this.selectedIntent.id]
          }
        })
        for (let i = 0; i < this.intents.length; i++) {
          if (this.intents[i] == this.selectedIntent) {
            this.intents.splice(i, 1)
            break;
          }
        }
        this.showCreate()
        this.notify('success', 'plugins.nlp.success.intent.delete')
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.intent.delete')
        console.error(e)
      }
    },

    onIntentInput: function () {
      if (this.intentCreate) {
        this.intents.push(this.selectedIntent)
        this.intentCreate = false
      } else {
        for(let i = 0; i<this.intents.length; i++) {
          if(this.selectedIntent.id === this.intents[i].id) {
            this.intents[i] = this.selectedIntent
            break
          }
        }
      }
    },

    showCreate: function () {
      this.selectedIntent = {
        name: '',
        confidence_save: 0.6,
        confidence_detect: 0.8,
        states_limit_on: false,
        training_phrases: [],
        parameters: [],
        states: [],
        statesTransitions: []
      }
      this.intentCreate = true
      this.whichView = 0
    },

    showEntities: function () {
      this.selectedIntent = null
      this.whichView = 1
    },

    showSavedPhrases () {
      this.selectedIntent = null
      this.whichView = 2
    },

    showSettings () {
      this.selectedIntent = null
      this.whichView = 3
    },

    isSelected (item) {
      if (item == this.selected) return true
    },

    getSelected (item) {
      if (this.selectedIntent == item) return
      this.selectedIntent = item
      this.intentCreate = false
      this.whichView = 0
    },

    guardEdited (viewAction) {
      if(this.$store.state.isEdited) {
        this.$refs.popupEditGuard.open()
        this.viewAction = viewAction
      } else {
        viewAction()
      } 
    },

    performViewAction() {
      this.viewAction()
      this.viewAction = null
      this.$store.commit('SET_EDITED', false)
      this.$refs.popupEditGuard.close()
    },

    cancelViewAction() {
      this.viewAction = null
      this.$refs.popupEditGuard.close()
    },

    async processTestPhrase () {
      try {
        const res = await axios.post('/api/plugin/nlp/test', {
          text: this.testStr
        })

        const confArray = []
        for(const key in res.data.confidence) {
          confArray.push({
            name: key,
            confidence: res.data.confidence[key].toFixed(3)
          })
        }
        
        this.testResult.confidences = confArray.sort((a, b) => b.confidence - a.confidence)
        this.testResult.phrase = res.data.phrase.join(' ')
        this.testResult.fuzzied = res.data.fuzzied

        this.showTestResult = true
      } catch (e) {
        console.error(e)
      }
    }
  },

  components: {
    'intent-editor': intent,
    'entities-editor': entities,
    'saved-phrases': savedPhrases,
    'nlp-settings': settings,
    'sel-popup': s_popup
  }
}
</script>

<style lang="scss">
.nlp {
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: 100%;

  &__sidebar {
    @include boxShadow(-2px, 0px, 15px, -13px, rgba(0, 0, 0, 0.75));
    background-color: $bgLightGrey;
    padding: 12px 12px;
    width: 292px;
    height: calc(100% - 24px);
    display: grid;
    grid-template-rows: 1fr min-content min-content;
  }

  &__sidebar-intents {
    display: grid;
    //align-items: start;
    align-content: start;
    grid-template-rows: min-content minmax(1fr, min-content);
    overflow: hidden;
  }

  &__sidebar-intents-title {
    font-size: 1.1em;
    font-weight: bold;
    margin-top: 4px;
    margin-bottom: 5px;
  }

  &__sidebar-intents-cont {
    padding: 2px 2px 0px 0px;
    overflow-y: scroll;
    display: grid;
    gap: 8px;

    & .card {
      border-radius: 2px !important;
    }

    & .card > .card__gradient {
      border-radius: 0px;
      width: 4px;
    }
  }

  &__sidebar-create {
    @include clickableText();
    margin-bottom: 10px;
  }

  &__sidebar-menu {
    display: grid;
    gap: 4px;
    margin-top: 10px;
    margin-bottom: 6px;
  }

  &__sidebar-menu-divider {
    justify-self: center;
    height: 1px;
    background: #DDDDDD;
    width: 100%;
  }

  &__sidebar-menu-item {
    display: grid;
    align-items: center;
    grid-template-columns: 4px 1fr;
    grid-template-rows: 40px;
    cursor: pointer;

    &:not(&--active) {
      & > .nlp__sidebar-menu-color {
        transform: scaleY(0);
      }
    }

    &:hover:not(&--active) {
      & > .nlp__sidebar-menu-color {
        transform: scaleY(0.5);
      }
    }
  }

  &__sidebar-menu-color {
    grid-row-start: 1;
    background: $primary;
    height: 100%;
    transition: transform 0.1s;
  }

  &__sidebar-menu-name {
    padding: 0px 10px;
    grid-row: 1/3;
    text-align: center;
    font-size: 1.0em;
    font-weight: bold;
  }

  &__test {
    position: relative;
  }

  &__editor {
    max-height: 100%;
    overflow: scroll;
  }

  &__test-popup {
    z-index: 10000;
    position: absolute;
    width: calc(100%);
    max-height: 500px;
    transform: translateY(calc(-100% - 6px));
    box-shadow: 0px 0px 13px 1px rgba(189,189,189,0.7);
    border-radius: 8px;

    display: grid;
    grid-template-columns: 3px 1fr;
    grid-template-rows: min-content 1fr min-content;
  }

  &__test-color {
    grid-column-start: 1;
    grid-row: 1 / last-line;
    background: $primary;
  }

  &__test-header {
    grid-row-start: 1;
    grid-column-start: 2;
    background: white;
    display: flex;
    justify-content: flex-end;
  }

  &__test-header-icon {
    color: $inactiveGrey;
    padding: 4px;
    margin-right: 6px;
    margin-top: 4px;
    cursor: pointer;
  }

  &__test-content {
    grid-column-start: 2;
    grid-row-start: 2;
    background: white;
    display: grid;
    //justify-content: start;
    grid-template-columns: min-content minmax(70%, 1fr);
    grid-template-rows: fit-content(1fr) fit-content(1fr) min-content fit-content(2fr);
    //grid-template-rows: min-content min-content min-content 1fr;
    grid-column-gap: 8px;
    grid-row-gap: 6px;
    padding: 10px;
    padding-top: 0px;
    padding-bottom: 4px;
    overflow-y: hidden;
  }

  &__test-label {
    font-weight: bold;
  }

  &__test-text {
    //display: flex;
    //align-items: flex-end;
    overflow-y: scroll;
  }

  &__test-conf {
    grid-column-start: 1;
    grid-column-end: 3;
  }

  &__test-conf-header {
    grid-column: 1 / 3;
    margin-top: 4px;
    padding-top: 6px;
    border-top: solid #e6e6e6 1px;
    font-weight: bold;
  }

  &__test-conf-content {
    grid-column: 1 / 3;

    margin-bottom: 4px;
    overflow-y: scroll;
    padding-right: 4px;

    display: grid;
    grid-template-columns: 1fr 30%; 
    grid-auto-flow: column;
    grid-row-gap: 4px;
  }

  &__test-conf-empty {
    grid-column-start: 1;
    grid-column-end: 3;
    text-align: center;
    font-style: italic;
    color: $inactiveGrey;
    padding-bottom: 2px;
  }

  &__test-conf-name {
    grid-column-start: 1;
  }

  &__test-conf-value {
    grid-column-start: 2;
    font-weight: bold;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
  }
}

.popup-subtext {
  text-align: center;
  margin-bottom: 18px;
}
</style>